import React, { useMemo } from 'react'
import { Grid } from '@material-ui/core'
import { Tooltip } from '@progress/kendo-react-tooltip'
import {
  DialogFormHLPure,
  ButtonAddHLPure,
  SearchBarHLPure,
  GridSelectionHLPure,
  CheckBoxHLPure,
  TextAreaHLPure
} from '../../pures'

/**
 * @component DialogShareUsersPure
 * @description
 * Modal reutilizable para compartir documentos o expedientes con colaboradores o invitados.
 * Incluye pestañas, búsqueda de usuarios, checkboxes de permisos y campo de mensaje.
 *
 * @param {Object} props
 * @property {Function} t - Función de traducción i18n.
 * @property {Object} documentData - Documento actual (para mostrar su nombre).
 * @property {Array} usersGrid - Lista de usuarios disponibles (colaboradores/invitados).
 * @property {Array} permisos - Permisos configurables.
 * @property {string} userSearch - Valor del input de búsqueda.
 * @property {string} messageShare - Mensaje opcional a enviar.
 * @property {Object} handlers - Conjunto de handlers provenientes del action principal.
 */
const DialogShareUsersPure = ({
  t,
  documentData,
  usersGrid = [],
  permisos = [],
  userSearch = '',
  messageShare = '',
  handlers = {},
  userSendGridSelected
}) => {

  const heightGrid = useMemo(() => {
    const maxHeight = 340
    const rowHeight = 70
    const count = usersGrid.length ? usersGrid.length : 1
    return count > 3 ? maxHeight : (60 + (count * rowHeight))
  }, [usersGrid.length])

  if (!handlers || typeof handlers !== 'object') return null

  return (
    <DialogFormHLPure
      titleDialog={`${t('documentPanelActions.dialog.share')} ${documentData?.name || ''}`}
      saveBtn={t('documentPanelActions.share')}
      textBtnCancel={t('button.cancel')}
      cancel={handlers.handleOpenCloseDialogShare}
      handleSubmit={handlers.handleSubmitDialogShare}
    >
      <div className='documentPanelActions-dialog-share-container'>
        {/* Tabs: Colaboradores / Invitados */}
        <div
          className='documentPanelActions-tabs'
          style={{ display: 'flex', gap: 12, marginBottom: 12 }}
          role='tablist'
        >
          <Tooltip openDelay={600} position='top' anchorElement='target' tooltipClassName='tooltipGeneralStyle'>
            <button
              role='tab'
              type='button'
              onClick={() => handlers.handleChangeTab(process.env.REACT_APP_USER_TAB_COLLABORATORS)}
              className={`tab-btn ${handlers.activeShareTab === (process.env.REACT_APP_USER_TAB_COLLABORATORS) || handlers.activeShareTab === undefined
                ? 'active'
                : ''
                }`}
            >
              {t('digitalVault.share.collaborators') || 'Colaboradores'}
            </button>
          </Tooltip>

          <Tooltip openDelay={600} position='top' anchorElement='target' tooltipClassName='tooltipGeneralStyle'>
            <button
              role='tab'
              type='button'
              onClick={() => handlers.handleChangeTab(process.env.REACT_APP_USER_TAB_INVITED)}
              className={`tab-btn ${handlers.activeShareTab === process.env.REACT_APP_USER_TAB_INVITED ? 'active' : ''}`}
            >
              {t('digitalVault.share.invited') || 'Invitados'}
            </button>
          </Tooltip>
        </div>

        {/* Botones de agregar y búsqueda */}
        <div className='dialogSigner-formButtons'>
          {(handlers.activeShareTab === process.env.REACT_APP_USER_TAB_COLLABORATORS || handlers.activeShareTab === undefined) && (
            <ButtonAddHLPure
              nameBtn={t('digitalVault.share.addCollaborator')}
              onClickBtn={handlers.handleOpenAddCollaborator}
            />
          )}

          {handlers.activeShareTab === process.env.REACT_APP_USER_TAB_INVITED && (
            <ButtonAddHLPure
              nameBtn={t('digitalVault.share.addInvited')}
              onClickBtn={handlers.handleOpenAddInvited}
            />
          )}

          <SearchBarHLPure
            placeholder={t('digitalVault.editDocument.placeholder.search')}
            onSearchChange={handlers.filterChangeUsers}
            searchValue={userSearch}
            icon={false}
          />
        </div>

        {/* Tabla de usuarios */}
        <div className='dialog-invitation-grid'>
          <Grid item className='documentPanelActions-grid-documents'>
            <GridSelectionHLPure
              styleGeneral
              styleGeneralClass={`dialog-signer-gridSelectionGLPure ${'dialog-signer-height' + heightGrid}`}
              pageable
              skip={0}
              take={4}
              dataGrid={usersGrid}
              filter
              onSelectedItem={userSendGridSelected}
              columnTwo={false}
              columnThree={false}
              columnFive={false}
              cellColumnOne={(props) => (
                <td className='documentPanelActions-cell-one'>
                  <div className='documentPanelActions-div-cell-one'>
                    <CheckBoxHLPure
                      valueCheckBox={props.dataItem}
                      checkedState={props.dataItem.checked}
                      onChangeHandler={(event) => handlers.onChangeCheckShare(event, props.dataItem)}
                    />
                    <div className='documentPanelActions-div-user'>
                      <span className='gridHLPure-userName'>{props.dataItem.name}</span>
                      <span className='gridHLPure-fieldSubtitle'>{props.dataItem.email}</span>
                    </div>
                  </div>
                </td>
              )}
            />
          </Grid>
        </div>

        {/* Permisos y mensaje */}
        <div className='documentPanelActions-div-actions-btns'>
          {(!handlers.activeShareTab || handlers.activeShareTab === process.env.REACT_APP_USER_TAB_COLLABORATORS) && (
            <h1>{t('documentPanelActions.permissions.share')}</h1>
          )}

          <div>
            {handlers.activeShareTab === process.env.REACT_APP_USER_TAB_INVITED ? (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 10 }}>
                {(() => {
                  const downloadPerm = (permisos || []).find((p) => p?.nameBD === 'download')
                  return (
                    <CheckBoxHLPure
                      idCheckBox='perm-invited-download'
                      labelCheckBox={t('digitalvault.share.allowGuestDownload')}
                      checkedState={!!downloadPerm?.checked}
                      onChangeHandler={(e) =>
                        downloadPerm &&
                        (handlers.onTogglePermission
                          ? handlers.onTogglePermission(e, downloadPerm)
                          : handlers.onClickBtnActions && handlers.onClickBtnActions(e, downloadPerm))
                      }
                    />
                  )
                })()}
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
                  gap: 12,
                  marginBottom: 10
                }}
              >
                {permisos.map((permiso, i) => (
                  <Tooltip
                    key={`perm-tp-${i}`}
                    openDelay={600}
                    position='top'
                    anchorElement='target'
                    tooltipClassName='tooltipGeneralStyle'
                  >
                    <div title={permiso.name} style={{ display: 'flex', justifyContent: 'center' }}>
                      <CheckBoxHLPure
                        idCheckBox={`perm-${i}`}
                        labelCheckBox={permiso.name}
                        checkedState={!!permiso.checked}
                        onChangeHandler={(e) =>
                          handlers.onTogglePermission
                            ? handlers.onTogglePermission(e, permiso)
                            : handlers.onClickBtnActions && handlers.onClickBtnActions(e, permiso)
                        }
                        stackVertical
                      />
                    </div>
                  </Tooltip>
                ))}
              </div>
            )}
          </div>

          <div className='documentPanelActions-dialog-message'>
            <h1>{t('documentPanelActions.message.share')}</h1>
            <TextAreaHLPure
              valueTextData={messageShare}
              handleChangeTextArea={handlers.handleChangeMessageShare}
            />
          </div>
        </div>
      </div>
    </DialogFormHLPure>
  )
}

export default DialogShareUsersPure
